import { useAuth0 } from '@auth0/auth0-react';
import StatusModal from '@axa-japan/design-system-react/StatusModal';
import Text from '@axa-japan/design-system-react/Text';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import fileIconUrl from '../assets/file_icon.svg';
import CustomLoader from '../components/common/CustomLoader';
import CustomModal from '../components/common/CustomModal';
import UrlWatcher from '../components/UrlWatcher';
import useGetConnectionType from '../hooks/useGetConnectionType';
import useIdleSessionHandler from '../hooks/useIdleSessionHandler';
import apiClient from '../libs/apiClient';
import { useApplicationContext } from '../providers/ApplicationProvider';
import { useUserContext } from '../providers/UserProvider';
import { AUTH_TYPE } from '../types/auth';
import MaintenancePage from './MaintenancePage';

const WHITE_LIST_URLS = ['/access-link/send-sms'];

const DesktopLayout = () => {
  const connectionType = useGetConnectionType();
  const { user, getAccessTokenSilently } = useAuth0();
  const { env, maintenance, setUnderMaintenance } = useApplicationContext();
  const { token, isLoading, tokenRefreshAt, isBulkProcessing, setLoader, setUser, forceReload } = useUserContext();
  const [hasToken, setHasToken] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isServerError, setIsServerError] = useState(false);

  const triggerSessionTimeout = () => {
    setIsErrorModalOpen(true);
    forceReload();
  };

  const { resetSession } = useIdleSessionHandler(triggerSessionTimeout);

  const getRefreshToken = async () => {
    if (tokenRefreshAt && tokenRefreshAt > Date.now()) {
      return token;
    }

    const refreshedToken = await getAccessTokenSilently(); // Refresh the token
    setUser(refreshedToken);

    return refreshedToken;
  };

  useEffect(() => {
    // to prevent premature api call before the apiClient interceptors is set
    // when token is not null, that means user has logged in, so the hasToken flag is set to true.
    // at this point, apiClient has already interceptor set.
    if (token) {
      setHasToken(true);
    }
  }, [token]);

  useEffect(() => {
    const requestIntercept = apiClient.interceptors.request.use(
      async (config) => {
        setLoader(true, config.url);
        if (!config.headers['Authorization']) {
          const refreshedToken = await getRefreshToken(); // Check and update the token if refreshAt expires
          resetSession();
          config.headers['Authorization'] = `Bearer ${refreshedToken}`;
          config.headers['User-Email'] = user?.email;
        }

        return config;
      },
      (error) => {
        setLoader(false);
        Promise.reject(error);
      },
    );

    const responseIntercept = apiClient.interceptors.response.use(
      (response) => {
        setLoader(false, response.config.url);
        return response;
      },
      (error) => {
        setLoader(false);
        if (error instanceof AxiosError) {
          const { status, config } = error;
          const method = config?.method?.toUpperCase();
          const url = config?.url;

          // Determine reusable conditions like method and whitelist url
          const isGetMethod = method === 'GET';
          const isWhitelistedUrl = url && WHITE_LIST_URLS.includes(url);

          // Handle specific HTTP status codes
          switch (status) {
            case 429:
              // Bypass if - parallel processing is enabled OR request method is GET
              if (!isBulkProcessing && !isGetMethod) {
                setIsServerError(true);
              }
              break;

            case 500:
              // Bypass if - parallel processing is enabled OR request method is GET OR url is from white list
              if (!isBulkProcessing && !isGetMethod && !isWhitelistedUrl) {
                setIsServerError(true);
              }
              break;

            case 503:
              setUnderMaintenance(true);
              break;

            default:
              // Handle other statuses or do nothing
              break;
          }
        }

        return Promise.reject(error);
      },
    );

    return () => {
      apiClient.interceptors.request.eject(requestIntercept);
      apiClient.interceptors.request.eject(responseIntercept);
    };
  }, [token, apiClient]);

  if (!hasToken) {
    // This will make sure that the api interceptor will be set before any api call.
    return <></>;
  }

  if (token) {
    if (maintenance) {
      return <MaintenancePage />;
    }

    return (
      <>
        <UrlWatcher />
        <StatusModal
          id="warning-server-error-modal"
          title="システムエラーが発生しました"
          open={isServerError}
          setOpen={setIsServerError}
          content="しばらくお待ちいただき、再度お試しください。"
          buttons={[
            {
              text: 'OK',
            },
          ]}
        />
        {(isLoading || isBulkProcessing) && <CustomLoader text="処理中です。しばらくお待ちください。" />}
        <CustomModal
          open={isErrorModalOpen}
          title="セッションタイムアウトしました。"
          onClose={() => window.location.reload()}
          buttonLabel="OK"
          isWarningModal
        >
          <>
            <Text>サービスご利用中に一定時間操作がありませんでした。そのため、自動的にサービスを終了しました。</Text>
            <Text>再度ご利用になる場合は、もう一度やり直してください。</Text>
          </>
        </CustomModal>
        <div className="min-h-screen flex flex-col">
          <nav className="p-4 border-b border-mercury">
            <div className="container mx-auto flex items-baseline justify-between">
              <div className="flex items-baseline space-x-2">
                {/* AXA Logo */}
                <img src="/axa_logo_text.png" alt="AXA Logo" className="h-8 lg:h-10 align-bottom" />
              </div>
              {env && <span className="bg-red-200 px-4 pt-1 border-red-500 border-spacing-1 rounded-sm font-semibold text-black">{env}</span>}
              {/* Flex-grow to push the span to the right */}
              <span className="text-xs text-right self-end">
                <a
                  href={`/docs${connectionType === AUTH_TYPE.Internal ? '/int/593f8d7ae2be' : '/ext/7b617ecbba7a'}/UploadService_Manual_CCIFA_AXA.pdf`}
                  className="text-xs text-axaBlue underline inline-flex items-end"
                  target="_"
                >
                  <span className="h-4">撮影およびアップロードマニュアル</span>
                  <img src={fileIconUrl} alt="File Icon Layout" className="h-4 ml-1 align-baseline" />
                </a>
              </span>
            </div>
          </nav>

          <div className="flex-grow my-8">
            <div className="container h-full mx-auto">
              <Outlet />
            </div>
          </div>

          <footer className="bg-interactiveVisitedOcean text-white p-4">
            <div className="mx-auto justify-between items-center flex w-full flex-row">
              {/* AXA Logo */}
              <img src="/axa_logo_solid_white.png" alt="AXA Logo" className="lg:my-4 h-8 lg:h-10" />
              {/* Copyright text */}
              <div className="flex flex-col">
                <span className="mt-5 text-xs text-center">Copyright &copy; AXA Life Insurance Co.,Ltd. All Rights Reserved.</span>
              </div>
            </div>
          </footer>
        </div>
      </>
    );
  }
};

export default DesktopLayout;
